'use client';

import { c as _c } from "react/compiler-runtime";
import { EVENT_DISPATCH_GA } from '@repo/analytics/analytics';
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useCallback, useEffect, useRef } from 'react';
import type { AdProviders } from '../types/ad-slot-props';
const AD_START_IMPRESSION_DELAY_MS = 1_000;
const FILL_TIMEOUT_DURATIONS_SEC = [5, 15] as const;
type AdStartDebounceState = {
  timeoutId: ReturnType<typeof setTimeout> | null;
  fired: boolean;
};
function getViewportSize(): {
  screenWidth: number;
  screenHeight: number;
} {
  return {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight
  };
}
function scheduleAdStartImpression(state: AdStartDebounceState, onFire: () => void, delayMs: number = AD_START_IMPRESSION_DELAY_MS): AdStartDebounceState {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
  }
  return {
    fired: state.fired,
    timeoutId: setTimeout(() => {
      onFire();
    }, delayMs)
  };
}
function cancelAdStartImpression(state: AdStartDebounceState): AdStartDebounceState {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
  }
  return {
    fired: state.fired,
    timeoutId: null
  };
}
function markAdStartFired(state: AdStartDebounceState): AdStartDebounceState {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
  }
  return {
    fired: true,
    timeoutId: null
  };
}
export function useAdAnalytics(analyticsLocation, provider) {
  const $ = _c(32);
  const analytics = useAnalytics();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      timeoutId: null,
      fired: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const debounceRef = useRef(t0);
  const slotRenderedFiredRef = useRef(false);
  const initializedFiredRef = useRef(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fillTimeoutIdsRef = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      for (const timeoutId of fillTimeoutIdsRef.current) {
        clearTimeout(timeoutId);
      }
      fillTimeoutIdsRef.current = [];
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const clearFillTimeouts = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      debounceRef.current = cancelAdStartImpression(debounceRef.current);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cancelAdStart = t3;
  let t4;
  if ($[4] !== analytics || $[5] !== analyticsLocation || $[6] !== provider) {
    t4 = () => {
      debounceRef.current = scheduleAdStartImpression(debounceRef.current, () => {
        debounceRef.current = markAdStartFired(debounceRef.current);
        analytics.event("ad_start", {
          location: analyticsLocation,
          provider
        });
      }, AD_START_IMPRESSION_DELAY_MS);
    };
    $[4] = analytics;
    $[5] = analyticsLocation;
    $[6] = provider;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onAdStart = t4;
  let t5;
  if ($[8] !== analytics || $[9] !== analyticsLocation || $[10] !== provider) {
    t5 = () => {
      if (slotRenderedFiredRef.current) {
        return;
      }
      slotRenderedFiredRef.current = true;
      analytics.event("programmable_ad_slot_rendered", {
        location: analyticsLocation,
        provider,
        ...getViewportSize()
      }, EVENT_DISPATCH_GA);
    };
    $[8] = analytics;
    $[9] = analyticsLocation;
    $[10] = provider;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const trackSlotRendered = t5;
  let t6;
  if ($[12] !== analytics || $[13] !== analyticsLocation || $[14] !== provider) {
    t6 = () => {
      if (initializedFiredRef.current) {
        return;
      }
      initializedFiredRef.current = true;
      analytics.event("programmable_ad_initialized", {
        location: analyticsLocation,
        provider,
        ...getViewportSize()
      }, EVENT_DISPATCH_GA);
    };
    $[12] = analytics;
    $[13] = analyticsLocation;
    $[14] = provider;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const trackInitialized = t6;
  let t7;
  if ($[16] !== analytics || $[17] !== analyticsLocation || $[18] !== provider) {
    t7 = () => {
      analytics.event("programmable_ad_empty", {
        location: analyticsLocation,
        provider,
        ...getViewportSize()
      }, EVENT_DISPATCH_GA);
    };
    $[16] = analytics;
    $[17] = analyticsLocation;
    $[18] = provider;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const trackEmpty = t7;
  let t8;
  if ($[20] !== analytics || $[21] !== analyticsLocation || $[22] !== provider) {
    t8 = () => {
      clearFillTimeouts();
      fillTimeoutIdsRef.current = FILL_TIMEOUT_DURATIONS_SEC.map(duration => setTimeout(() => {
        analytics.event("programmable_ad_fill_timeout", {
          location: analyticsLocation,
          provider,
          duration,
          ...getViewportSize()
        }, EVENT_DISPATCH_GA);
      }, duration * 1000));
    };
    $[20] = analytics;
    $[21] = analyticsLocation;
    $[22] = provider;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const startFillTimeouts = t8;
  let t10;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => () => {
      cancelAdStart();
      clearFillTimeouts();
    };
    t10 = [cancelAdStart, clearFillTimeouts];
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== onAdStart || $[27] !== startFillTimeouts || $[28] !== trackEmpty || $[29] !== trackInitialized || $[30] !== trackSlotRendered) {
    t11 = {
      onAdStart,
      cancelAdStart,
      trackSlotRendered,
      trackInitialized,
      trackEmpty,
      startFillTimeouts,
      clearFillTimeouts
    };
    $[26] = onAdStart;
    $[27] = startFillTimeouts;
    $[28] = trackEmpty;
    $[29] = trackInitialized;
    $[30] = trackSlotRendered;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}